
import { IcdCorporationManager } from '@herbjoy/apex-api-models';
import angular, { IQService } from 'angular';
import { StateService, Transition } from 'angular-ui-router';
import { configureStates, resolveFromParam, RouteDeclaration } from '../../core/router/router-helper.provider';
import { UtilityService } from '../../core/services/utility.service';

enum IcdCorporationManagerRoles {
    Hub = 13,
    NationalConsultant = 14,
    RegionalConsultant = 15,
    SCAuditor = 16,
    PromotingOwner = 26
}

interface CorporationManagerComponentConfig {
    moduleName: string;
    roleID: IcdCorporationManagerRoles;
    roleName: string;
    serviceName: string;
    urlPath: string;
    // Method on the service for getting a list of the managers.
    listMethodName: string;
    // Method on the service for getting a single manager.
    detailMethodName: string;
    createMethodName: string;
    updateMethodName: string;
    deleteMethodName: string;
}

function createIcdManagerRoutes(config: CorporationManagerComponentConfig): RouteDeclaration[] {
    return [{
        // Legacy routes
        state: `organization.list${config.moduleName}s`,
        config: {
            name: `organization.list${config.moduleName}s`,
            url: `^/Organization/List${config.moduleName}s`,
            redirectTo: `icdCorporationManager.${config.moduleName}.list`
        }
    }, {
        // Legacy routes
        state: `organization.edit${config.moduleName}`,
        config: {
            name: `organization.edit${config.moduleName}`,
            url: `^/Organization/Edit${config.moduleName}/:id`,
            params: {
                id: { type: "int", value: null, squash: true }
            },
            resolve: {
                id: resolveFromParam("id")
            },
            redirectTo: (transition: Transition) => {
                const { id } = transition.params();
                return id ? `icdCorporationManager.${config.moduleName}.edit`
                    : `icdCorporationManager.${config.moduleName}.new`;
            }
        }
    }, {
        state: `icdCorporationManager.${config.moduleName}`,
        config: {
            name: `icdCorporationManager.${config.moduleName}`,
            url: `/${config.urlPath}`,
            resolve: {
                config: () => config,
                service: [config.serviceName, svc => svc]
            },
            abstract: true
        }
    }, {
        state: `icdCorporationManager.${config.moduleName}.list`,
        config: {
            name: `icdCorporationManager.${config.moduleName}.list`,
            url: ``,
            component: "apexCorporationManagerList",
            resolve: {
                name: () => config.roleName,
                managers: service => {
                    'ngInject';
                    return service[config.listMethodName]();
                },
                onDeleteManager: ($q: IQService, $state: StateService, utilityService: UtilityService, service) => {
                    'ngInject'
                    return ["$event", ({ manager }: { manager: IcdCorporationManager }) => {
                        if (!confirm("Are you sure?")) {
                            return $q.reject({ $value: "cancel" });
                        }
                        // Load the service's delete method for this type of manager.
                        const deleted = service[config.deleteMethodName](manager.Id)
                            .then(() => {
                                // Reload the grid.
                                $state.go(".", null, { reload: true });
                            });
                        utilityService.showLoadingUntilResolved(
                            utilityService.alertOnError(deleted, "Delete failed")
                        );
                        return deleted;
                    }]
                }
            },
            data: {
                pageHeader: `${config.roleName} List`
            }
        }
    }, {
        state: `icdCorporationManager.${config.moduleName}.edit`,
        config: {
            name: `icdCorporationManager.${config.moduleName}.edit`,
            url: `/:id/edit`,
            component: "apexEditCorporationManager",
            resolve: {
                roleID: () => config.roleID,
                roleName: () => config.roleName,
                id: resolveFromParam("id"),
                manager: (service, id) => {
                    'ngInject';
                    return service[config.detailMethodName](id);
                },
                onSaveManager: (utilityService: UtilityService, service) => {
                    'ngInject';
                    return ["$event", ({ manager }: { manager: IcdCorporationManager }) => {
                        const saved = service[config.updateMethodName](manager);
                        utilityService.showLoadingUntilResolved(
                            utilityService.alertOnError(saved, "Saving failed")
                        );
                        return saved;
                    }]
                }
            },
            data: {
                pageHeader: `Edit ${config.roleName}`
            }
        }
    }, {
        state: `icdCorporationManager.${config.moduleName}.new`,
        config: {
            name: `icdCorporationManager.${config.moduleName}.new`,
            url: `/new`,
            component: "apexEditCorporationManager",
            resolve: {
                roleID: () => config.roleID,
                roleName: () => config.roleName,
                manager: () => ({
                    IsNew: true
                }),
                onSaveManager: ($state: StateService, utilityService: UtilityService, service) => {
                    'ngInject';
                    return ["$event", ({ manager }: { manager: IcdCorporationManager }) => {
                        const saved = service[config.createMethodName](manager);
                        utilityService.showLoadingUntilResolved(
                            utilityService.alertOnError(saved, "Creating failed")
                        );
                        return saved.then((manager: IcdCorporationManager) => {
                            if (manager.Id) {
                                $state.go("^.edit", { id: manager.Id });
                            } else {
                                $state.go("^.list");
                            }
                        })
                    }];
                }
            },
            data: {
                pageHeader: `Add New ${config.roleName}`
            }
        }
    }];
}

const routes: RouteDeclaration[] = [
    {
        state: "icdCorporationManager",
        config: {
            name: "icdCorporationManager",
            abstract: true,
            url: "/admin/corporation-management"
        }
    },
    ...createIcdManagerRoutes({
        moduleName: "auditor",
        roleID: IcdCorporationManagerRoles.SCAuditor,
        roleName: "SC Auditor",
        serviceName: "auditorService",
        listMethodName: "retrieveAuditors",
        detailMethodName: "retrieveAuditor",
        createMethodName: "saveAuditor",
        updateMethodName: "saveAuditor",
        deleteMethodName: "deleteAuditor",
        urlPath: "sc-auditors"
    }),
    ...createIcdManagerRoutes({
        moduleName: "nationalConsultant",
        roleID: IcdCorporationManagerRoles.NationalConsultant,
        roleName: "National Consultant",
        serviceName: "nationalConsultantService",
        listMethodName: "retrieveNationalConsultants",
        detailMethodName: "retrieveNationalConsultant",
        createMethodName: "saveNationalConsultant",
        updateMethodName: "saveNationalConsultant",
        deleteMethodName: "deleteNationalConsultant",
        urlPath: "national-consultants"
    }),
    ...createIcdManagerRoutes({
        moduleName: "regionalConsultant",
        roleID: IcdCorporationManagerRoles.RegionalConsultant,
        roleName: "Regional Consultant",
        serviceName: "regionalConsultantService",
        listMethodName: "retrieveRegionalConsultants",
        detailMethodName: "retrieveRegionalConsultant",
        createMethodName: "saveRegionalConsultant",
        updateMethodName: "saveRegionalConsultant",
        deleteMethodName: "deleteRegionalConsultant",
        urlPath: "regional-consultants"
    }),
    ...createIcdManagerRoutes({
        moduleName: "hub",
        roleID: IcdCorporationManagerRoles.Hub,
        roleName: "Hub",
        serviceName: "hubService",
        listMethodName: "retrieveHubs",
        detailMethodName: "retrieveHub",
        createMethodName: "saveHub",
        updateMethodName: "saveHub",
        deleteMethodName: "deleteHub",
        urlPath: "hubs"
    }),
    ...createIcdManagerRoutes({
        moduleName: "promotingOwner",
        roleID: IcdCorporationManagerRoles.PromotingOwner,
        roleName: "Promoting Owner",
        serviceName: "promotingOwnerService",
        listMethodName: "retrievePromotingOwners",
        detailMethodName: "retrievePromotingOwner",
        createMethodName: "savePromotingOwner",
        updateMethodName: "savePromotingOwner",
        deleteMethodName: "deletePromotingOwner",
        urlPath: "promoting-owners"
    })
];

export const IcdCorporationManagerRoutingModule = angular
    .module("apex.mainApp.icdCorporationManager.routing", [])
    .run(configureStates(routes))
    .name;
